// src/axiosInstance.js
// Includes fullURL logging, detailed interceptor logging, AND restored email service logic.

import axios from "axios";
import { supabase } from './backend/supabase'; // Ensure path is correct relative to axiosInstance.js
import { emailApiService } from './backend/emailApiService'; // *** RESTORED IMPORT *** Ensure path is correct

// Separate base URLs for each service
const MAIN_API_URL = process.env.NODE_ENV === 'production'
  ? "https://api.lawdie.co"  // Production API endpoint
  : "http://localhost:4000"; // Development endpoint

const EMAIL_API_URL = process.env.NODE_ENV === 'production'
  ? "https://api.lawdie.co"  // Production email endpoint
  : "http://localhost:4001"; // Development email endpoint

// Helper to determine the correct base URL based on the request
const getBaseURL = (config) => {
  // Check if the request is going to the email service
  if (config?.url?.includes('/api/send-email') ||
      config?.url?.includes('/api/send-referral-email')) {
    console.log('[axiosInstance getBaseURL] Routing to EMAIL_API_URL'); // Log which URL is chosen
    return EMAIL_API_URL;
  }
  console.log('[axiosInstance getBaseURL] Routing to MAIN_API_URL');
  return MAIN_API_URL;
};

// Create a default axios instance
const axiosInstance = axios.create({
  withCredentials: true // Important for sending cookies/session info cross-origin
});

// Create an audio-specific axios instance (if used elsewhere)
export const audioAxiosInstance = axios.create({
  responseType: 'blob', // Important for audio data
  headers: {
    "Accept": "audio/*" // Set appropriate Accept header for audio
  },
  withCredentials: true
});

// --- Request Interceptor (with detailed logging and email service logic) ---
const setupRequestInterceptor = (instance) => {
  instance.interceptors.request.use(
    async (config) => {
      console.log(
        `[axiosInstance Interceptor] >>> Interceptor START for URL: ${config.url}`
      );
      try {
        // Dynamically set the baseURL based on the target service
        config.baseURL = getBaseURL(config); // This now logs internally
        console.log(
          `[axiosInstance Interceptor] Base URL actually set to: ${config.baseURL}`
        );

        // Check if Auth header was already set by the caller (e.g., AuthCallback)
        const authHeaderAlreadySet = !!config.headers?.Authorization;
        if (authHeaderAlreadySet) {
          console.log(
            "[axiosInstance Interceptor] Authorization header was ALREADY SET by caller."
          );
        } else {
          // Attempt to get Supabase session ONLY if header wasn't set by caller
          console.log(
            "[axiosInstance Interceptor] Auth header not set by caller. Attempting supabase.auth.getSession()..."
          );
          const {
            data: { session },
            error: sessionError,
          } = await supabase.auth.getSession();
          console.log(
            "[axiosInstance Interceptor] supabase.auth.getSession() completed."
          );

          if (sessionError) {
            console.error(
              "[axiosInstance Interceptor] Supabase Session error during getSession():",
              sessionError
            );
            console.warn(
              "[axiosInstance Interceptor] Proceeding without session due to error."
            );
          } else if (session?.access_token) {
            console.log(
              "[axiosInstance Interceptor] Setting Authorization header using token from interceptor session."
            );
            config.headers.Authorization = `Bearer ${session.access_token}`;
          } else {
            console.warn(
              "[axiosInstance Interceptor] No Supabase auth token found by interceptor via getSession() for request to",
              config.url
            );
          }
        }
        // --- End conditional session check ---


        // --- RESTORED: Email Service Specific Headers (Example - Adapt if needed) ---
        // Check if it's a production environment AND the URL matches email service endpoints
        const isEmailServiceUrl = config.url?.includes('/api/send-email') || config.url?.includes('/api/send-referral-email');

        if (process.env.NODE_ENV === 'production' && isEmailServiceUrl && config.baseURL === EMAIL_API_URL) {
          console.log('[axiosInstance Interceptor] Adding Production Email Service Headers...');
          const timestamp = Date.now().toString();
          // Ensure emailApiService and generateSignature method exist and work as expected
          if (emailApiService && typeof emailApiService.generateSignature === 'function') {
              try {
                  // *** Make sure generateSignature expects these parameters ***
                  const signature = await emailApiService.generateSignature(
                     timestamp,
                     config.method.toUpperCase(),
                     config.url, // Relative URL
                     config.data // Request body
                  );
                  config.headers = {
                    ...config.headers,
                    'X-API-Key': process.env.REACT_APP_EMAIL_API_KEY, // Ensure correct env var name
                    'X-Timestamp': timestamp,
                    'X-Signature': signature
                  };
                  console.log('[axiosInstance Interceptor] Email Service Headers Added.');
              } catch (sigError) {
                  console.error('[axiosInstance Interceptor] Error generating email API signature:', sigError);
                  // Decide how to handle signature generation errors - maybe throw or just warn
              }
          } else {
              console.warn('[axiosInstance Interceptor] emailApiService or generateSignature not available for email headers.');
          }
        }
        // --- End Email Service Headers ---


        // --- Detailed Request Logging ---
        console.log("[axiosInstance] Request config (PRE-SEND):", {
          baseURL: config.baseURL,
          url: config.url,
          fullURL: `${config.baseURL}${config.url}`,
          method: config.method?.toUpperCase(),
          headers: {
            ...config.headers,
            Authorization: config.headers?.Authorization ? "Bearer [REDACTED]" : "Not Set",
            'X-Signature': config.headers?.['X-Signature'] ? '[PRESENT]' : undefined, // Check for custom headers
            'X-API-Key': config.headers?.['X-API-Key'] ? '[PRESENT]' : undefined,
            'X-Timestamp': config.headers?.['X-Timestamp'] || undefined
          },
          params: config.params,
          hasData: !!config.data,
          responseType: config.responseType || "json",
          withCredentials: config.withCredentials,
        });
        // --- End Detailed Request Logging ---

        console.log(
          "[axiosInstance Interceptor] <<< Interceptor END (config looks okay, returning)"
        );
        return config;
      } catch (error) {
        console.error(
          "[axiosInstance Interceptor] !!! ERROR within request interceptor try block:",
          error
        );
        return config;
      }
    },
    (error) => {
      console.error(
        "[axiosInstance Interceptor] !!! Request setup error (before sending):",
        error
      );
      return Promise.reject(error);
    }
  );
};
// --- End Request Interceptor ---

// --- Response Interceptor ---
const setupResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    response => {
      return response; // Pass through successful responses
    },
    error => {
      console.error('[axiosInstance] Response Error Interceptor Caught Error:', error);
      if (error.response) {
        console.error('  Response Status:', error.response.status);
        console.error('  Response Data:', error.response.data);
        if (error.response.status === 401) {
          console.error('  Authorization Error Details:', {
            requestURL: error.config?.url,
            hasSentAuthHeader: !!error.config?.headers?.Authorization,
            responseMessage: error.response.data?.error || error.response.data?.message || 'No specific error message.'
          });
        }
      } else if (error.request) {
        console.error('  Error: No response received from server.', error.request);
         if (error.code === 'ECONNABORTED'){
             console.error('  Error Detail: Request timed out.');
         } else if (error.code === 'ERR_NETWORK') {
              console.error('  Error Detail: Network error (e.g., server down, CORS issue not caught by preflight).');
         } else {
             console.error('  Error Code:', error.code);
         }
      } else {
        console.error('  Error Message:', error.message);
      }
      return Promise.reject(error);
    }
  );
};
// --- End Response Interceptor ---

// Setup interceptors for both instances
setupRequestInterceptor(axiosInstance);
setupRequestInterceptor(audioAxiosInstance); // If using audio instance
setupResponseInterceptor(axiosInstance);
setupResponseInterceptor(audioAxiosInstance); // If using audio instance

export default axiosInstance; // Export the main instance